import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import HTMLWrapper from '@comp/Question/HTMLWrapper';
import RadioButton from '@base/RadioButton/RadioButton';
import Checkbox from '@base/Checkbox/Checkbox';
import NumberField from '@base/NumberField/NumberField';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { setCurrentSelectedAnswer } from '@actions/quizTaking';
import {
  getCurrentQuestionInfo,
  getCurrentSelectedAnswer,
  getCurrentQuestion,
  getQuizQuestionAnswers
} from '@selectors/quizTaking';
import isEmptyUtil from '@utils/isEmpty';
import { Spacings } from '@styles/index';
import { QUESTION_TYPE_MAP } from '@constants/quiz';
import useMaxAttemptQuestions from '@cont/Quiz/Taking/Common/useMaxAttemptQuestions';
import CheckedRadio from '@icon/CheckedRadio';
import EmptyRadio from '@icon/EmptyRadio';
import CheckboxFilled from '@icon/CheckboxFilled';
import EmptyCheckbox from '@icon/EmptyCheckbox';
import Dangerous from '@educator/Container/AutoDPP/DangerousContent';
import useQuestionStatus from '../Navigation/useQuestionStatus';

const OptionsWrapper = styled.div`
  display: grid;
  gap: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};
  grid-template-columns: ${({ $hasComprehension }) =>
    $hasComprehension ? '1fr' : '1fr 1fr'};
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? 'var(--color-base-light-0)' : 'none'};
  user-select: none;
  padding: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_1B};
  border: ${({ $isSelected }) =>
    $isSelected
      ? '1px solid var(--color-base-fill)'
      : '1px solid var(--color-divider);'};
`;

const Options = ({ code }) => {
  const dispatch = useDispatch();

  const currentQuestion = useSelectorWithShallowEqual(getCurrentQuestion);
  const { sectionUID, questionUID } = useSelectorWithShallowEqual(
    getCurrentQuestionInfo
  );
  const answers = useSelectorWithShallowEqual(
    getQuizQuestionAnswers(sectionUID, questionUID)
  );
  const currentSelectedAnswer = useSelectorWithShallowEqual(
    getCurrentSelectedAnswer
  );

  const { isAttempted } = useQuestionStatus(sectionUID, questionUID);

  const { isQuestionAttemptable } = useMaxAttemptQuestions(sectionUID);

  useEffect(() => {
    dispatch(setCurrentSelectedAnswer([...answers]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  const isOptionDisabled = () => {
    if (!isAttempted && !isQuestionAttemptable) {
      return true;
    }
    return false;
  };

  const handleAnswerChange = (_, value) => {
    const data = isEmptyUtil(value) ? [] : [value];

    dispatch(setCurrentSelectedAnswer(data));
  };

  const handleOptionClick = (e) => {
    if (isOptionDisabled()) {
      return;
    }

    const uid = e.currentTarget.dataset.answerUid;

    if (!uid) {
      return;
    }

    if (currentQuestion.type === QUESTION_TYPE_MAP.SINGLE) {
      if (currentSelectedAnswer[0] === uid) {
        return dispatch(setCurrentSelectedAnswer([]));
      }

      return dispatch(setCurrentSelectedAnswer([uid]));
    }

    if (currentQuestion.type === QUESTION_TYPE_MAP.MCQ) {
      if (currentSelectedAnswer.includes(uid)) {
        return dispatch(
          setCurrentSelectedAnswer(
            currentSelectedAnswer.filter((currentUID) => currentUID !== uid)
          )
        );
      }

      return dispatch(
        setCurrentSelectedAnswer([...currentSelectedAnswer, uid])
      );
    }
  };

  if (currentQuestion.type === QUESTION_TYPE_MAP.INTEGER) {
    return (
      <div>
        <NumberField
          fullWidth
          allowNegative
          allowFloat
          autoFocus
          placeholder="Type your answer here"
          onChange={handleAnswerChange}
          value={currentSelectedAnswer[0]}
          disabled={isOptionDisabled()}
        />
      </div>
    );
  }

  if (
    currentQuestion.type === QUESTION_TYPE_MAP.MCQ ||
    currentQuestion.type === QUESTION_TYPE_MAP.SINGLE
  ) {
    const getInputComponent = (answer, isSelected) => {
      if (currentQuestion.type === QUESTION_TYPE_MAP.SINGLE) {
        return (
          <RadioButton
            id={`answer-${answer.rank}`}
            checked={isSelected}
            value={answer.uid}
            disabled={isOptionDisabled()}
            icon={
              <EmptyRadio
                size="20px"
                color="var(--color-divider)"
                strokeWidth="2"
              />
            }
            checkedIcon={
              <CheckedRadio size="20px" color="var(--color-text-primary)" />
            }
          />
        );
      }

      return (
        <Checkbox
          id={`answer-${answer.rank}`}
          checked={isSelected}
          value={answer.uid}
          disabled={isOptionDisabled()}
          icon={
            <EmptyCheckbox
              size="20px"
              color="var(--color-divider)"
              strokeWidth="2"
            />
          }
          checkedIcon={
            <CheckboxFilled size="20px" boxColor="var(--color-text-primary)" />
          }
        />
      );
    };

    const hasComprehension = !!currentQuestion.comprehension[code];

    return (
      <OptionsWrapper $hasComprehension={hasComprehension} data-image-preview>
        {currentQuestion.answers?.map((answer) => {
          const isSelected = currentSelectedAnswer.includes(answer.uid);

          return (
            <Option
              key={answer.uid}
              data-answer-uid={answer.uid}
              onClick={handleOptionClick}
              $isSelected={isSelected}
              $isDisabled={isOptionDisabled()}
            >
              {getInputComponent(answer, isSelected)}

              <Dangerous
                as={HTMLWrapper}
                data-image-preview
                $isImageClickable={false}
                html={answer.content[code]}
              />
            </Option>
          );
        })}
      </OptionsWrapper>
    );
  }

  return null;
};

export default Options;

Options.propTypes = {
  code: PropTypes.number.isRequired
};
